/**
 * @module experience/shop/hardgoods
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
import {
  Product,
  ItemDetail,
  ColorOption,
  Specification,
  SkuPriceDetails,
  PricingOptions,
  MemoryOption,
} from '@support/api/common/product.interface';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { DeviceDetails } from '@experience/shop/hardgoods/interfaces/browse-hardgood.interface';
// Constants
import { HARDGOODS_CONSTANTS } from '@experience/shop/hardgoods/constants/browse-hardgoods.constants';

export class BrowseDeviceModel {
  /**
   * Holds family name
   */
  public familyName: string;
  /**
   * Holds family name
   */
  public canonicalUrl: string;
  /**
   * Holds offer Id
   */
  public offerId: string;
  /**
   * Holds boolean if product is featured
   */
  public isFeatured: boolean;
  /**
   * Holds sku level details
   */
  public itemDetails: DeviceDetails[] = [];
  /**
   * Holds boolean if it has deal
   */
  public hasDeal: boolean;
  /**
   * Holds review count number
   */
  public reviewsCount: number;
  /**
   * Holds product type
   */
  public productType: string;
  /**
   * Holds product type
   */
  public productSubType: string;
  /**
   * Holds boolean if device is accessory
   */
  public isAccessory: boolean;
  /**
   * Holds list of color options available for the product
   */
  public colorOptions: ColorOption[] = [];
  /**
   * Holds boolean if it has sale
   */
  public hasSale: boolean;
  /**
   * Holds overall rating
   */
  public averageOverallRating: string;
  /**
   * Holds device specifications
   */
  public specifications: Specification[];
  /**
   * Boolean to check if reviews are present for that device
   */
  public isReviewPresent: boolean;
  /**
   * Boolean to isEpin
   */
  public isEpin: boolean;
  /**
   * Holds manufacturer for that device
   */
  public manufacturer: string;
  /**
   * Stores the pricing options
   */
  public pricingOptions: PricingOptions[];
  /**
   * Holds boolean and set to true if it is Sim Card
   */
  public isSimCard: boolean;
  /**
   * Holds API response to cache used in 2101 response
   */
  public product: Product;
  /**
   * Holds list of memory options available for the product
   */
  public memoryOptions: MemoryOption[] = [];
  /**
   * Holds deal Promotion if available
   */
  private dealPromotion: SkuPriceDetails = {};
  // tslint:disable max-line-length cyclomatic-complexity
  constructor(
    product: Product,
    private objectService: ObjectService,
    private extTmoImagesUrl: string,
    private showUpdatedBrowseDevices?: boolean
  ) {
    this.product = product;
    this.familyName = this.objectService.get(product, 'name');
    this.productSubType = this.objectService.get(product, 'productSubType') || '';
    this.offerId = this.objectService.get(product, 'offerId');
    this.isFeatured = this.objectService.get(product, 'isFeatured');
    this.reviewsCount = this.objectService.get(product, 'reviewsCount') || 0;
    this.productType = this.objectService.get(product, 'productType') || '';
    this.isAccessory = this.productType.toUpperCase() === 'ACCESSORY';
    this.isReviewPresent = this.reviewsCount > 0;
    this.canonicalUrl = this.objectService.get(product, 'canonicalUrl');
    this.hasDeal = this.objectService.get(product, 'hasDeal');
    this.colorOptions = this.objectService.get(product, 'colorOptions');
    this.memoryOptions = this.objectService.get(product, 'memoryOptions');
    this.averageOverallRating = this.objectService.get(product, 'averageStarRating', 0);
    this.specifications = this.objectService.get(product, 'specifications');
    this.manufacturer = this.objectService.get(product, 'manufacturer');
    this.isEpin =
      this.productSubType.toUpperCase() === HARDGOODS_CONSTANTS.VOUCHER &&
      this.productType.toLowerCase() === HARDGOODS_CONSTANTS.FINANCIAL;
    this.isSimCard = this.productSubType.toUpperCase() === HARDGOODS_CONSTANTS.SIMCARD;
    this.pricingOptions = this.objectService.get(product, 'pricingOptions') || [];
    if (
      !this.isEpin &&
      !this.isSimCard &&
      this.objectService.get(product, 'colorOptions.length') &&
      !this.showUpdatedBrowseDevices
    ) {
      product.colorOptions.forEach((colorOption: ColorOption) => {
        if (this.objectService.get(product, 'itemDetails.length')) {
          const device = product.itemDetails.find((value: ItemDetail) => {
            return colorOption.name === value.color;
          });
          if (device) {
            this.setItemDetails(device);
          }
        }
      });
    } else if (
      this.showUpdatedBrowseDevices &&
      !this.isSimCard &&
      !this.isEpin &&
      !this.isAccessory
    ) {
      const memoryLength = this.memoryOptions && this.memoryOptions.length;
      if (this.objectService.get(product, 'itemDetails.length')) {
        const itemDetails = product.itemDetails.filter((value: ItemDetail) => {
          return value.memory === this.memoryOptions[memoryLength - 1].value;
        });
        itemDetails &&
          itemDetails.forEach((value: ItemDetail) => {
            this.setItemDetails(value);
          });
      }
    } else if (this.objectService.get(product, 'itemDetails.length')) {
      this.setItemDetails(product.itemDetails[0]);
    }
  }

  // tslint:disable max-line-length cyclomatic-complexity
  public setItemDetails(itemDetails: ItemDetail, memoryUpdate?: boolean, index?: number): void {
    const colorSwatchURL = this.objectService.get(itemDetails, 'skuImageUrl');
    const imageUrl = this.objectService.get(itemDetails, 'mainImageUrl');
    const priceWithService = this.getPricingOptions(itemDetails, 'WithService');
    const priceWithoutService = this.getPricingOptions(itemDetails, 'WithoutService');
    const listPriceWithService =
      this.objectService.get(priceWithService, 'priceSummary.payNowPrice.listPrice.amount') || 0;
    const salesPriceWithService =
      this.objectService.get(priceWithService, 'priceSummary.payNowPrice.salePrice.amount') || 0;
    const serviceThresholdPrice =
      this.objectService.get(priceWithService, 'serviceThreshold.amount') || 0;
    const salesPriceWithOutService =
      this.objectService.get(priceWithoutService, 'priceSummary.payNowPrice.salePrice.amount') || 0;
    const listPriceWithOutService =
      this.objectService.get(priceWithoutService, 'priceSummary.payNowPrice.listPrice.amount') || 0;
    this.hasDeal = this.objectService.get(itemDetails, 'hasDeal');
    this.hasSale = this.objectService.get(itemDetails, 'hasSale');
    if (this.hasSale && this.hasDeal) {
      this.hasSale = true;
      this.hasDeal = false;
    }
    if (this.objectService.get(itemDetails, 'skuPrice')) {
      this.dealPromotion = itemDetails.skuPrice.filter((skuPriceDetails: SkuPriceDetails) => {
        return (
          this.objectService.get(skuPriceDetails, 'priceListLineId', '').toLowerCase() ===
          HARDGOODS_CONSTANTS.WITH_SERVICE
        );
      })[0];
    }
    const item = {
      colorName: this.objectService.get(itemDetails, 'color'),
      uSkuIndicator: this.objectService.get(itemDetails, 'uSkuIndicator'),
      id: this.objectService.get(itemDetails, 'id'),
      isDefaultSKU: this.objectService.get(itemDetails, 'isDefaultSKU'),
      colorSwatchURL: this.extTmoImagesUrl && this.extTmoImagesUrl.concat(colorSwatchURL),
      availabilityStatus: this.objectService.get(itemDetails, 'availability.availabilityStatus'),
      imageUrl: this.extTmoImagesUrl && this.extTmoImagesUrl.concat(imageUrl),
      promotionDescription: this.objectService.get(
        this.dealPromotion,
        'promotions.0.longDescription'
      ),
      payNowPrice:
        this.isAccessory || this.isSimCard ? salesPriceWithOutService : salesPriceWithService,
      listPrice:
        this.isAccessory || this.isSimCard ? listPriceWithOutService : listPriceWithService,
      serviceThresholdPrice: serviceThresholdPrice,
      salesPriceWithOutService,
      strikeThroughPrice:
        this.isAccessory || this.isSimCard ? listPriceWithOutService : listPriceWithService,
      listPriceWithOutService: listPriceWithOutService,
      hasSale: this.hasSale,
      hasDeal: this.hasDeal,
      bannerText: this.objectService.get(itemDetails, 'bannerText'),
      conditionalEnhancement: this.objectService.get(itemDetails, 'conditionalEnhancement'),
      dealText: this.objectService.get(itemDetails, 'dealText'),
      memory: this.objectService.get(itemDetails, 'memory'),
      deviceSimInfo: this.objectService.get(itemDetails, 'deviceSimInfo'),
      legalText: this.objectService.get(itemDetails, 'legalText'),
      estimatedShippingFromDateTime: this.objectService.get(
        itemDetails,
        'availability.estimatedShippingFromDateTime'
      ),
      estimatedShippingToDateTime: this.objectService.get(
        itemDetails,
        'availability.estimatedShippingToDateTime'
      ),
      priceListLineId: this.getServicetype(itemDetails).priceListLineId,
    };
    if (memoryUpdate && index === 0) {
      this.itemDetails = [];
    }
    this.itemDetails.push(item);
  }

  /**
   * FUnction used to get pricing options
   * @param {ItemDetail} itemDetails
   * @param {string} serviceType
   * @returns {SkuPriceDetails}
   */
  private getPricingOptions(itemDetails: ItemDetail, serviceType: string): SkuPriceDetails {
    if (this.objectService.get(itemDetails, 'skuPrice')) {
      return itemDetails.skuPrice.find((value: SkuPriceDetails) => {
        return (
          value.priceListLineId === serviceType &&
          value.pricingOptionName === HARDGOODS_CONSTANTS.FULLPRICE
        );
      });
    }
  }

  private getServicetype(itemDetails: ItemDetail): SkuPriceDetails {
    const skuPrice = this.objectService.get(itemDetails, 'skuPrice');
    if (skuPrice) {
      return skuPrice.find(
        (skuPriceDetails: SkuPriceDetails) =>
          skuPriceDetails.priceListLineId == HARDGOODS_CONSTANTS.WITH_SERVICE ||
          skuPriceDetails.priceListLineId == HARDGOODS_CONSTANTS.WITHOUT_SERVICE
      );
    }
  }
}
